import React from 'react';
import PropTypes from 'prop-types';
import { DragSource, DropTarget } from 'react-dnd';

import { orange } from 'utils/color-styles';

const makeTarget = (isBefore) => ({
  canDrop(props, monitor) {
    const { index: draggedIndex } = monitor.getItem();
    let { index: overIndex } = props;
    if (isBefore) {
      overIndex -= 1;
    }
    const resultingOverIndex = overIndex >= draggedIndex ? overIndex : overIndex + 1;
    return draggedIndex !== resultingOverIndex;
  },
  drop(props, monitor) {
    const { index: draggedIndex } = monitor.getItem();
    let { index: overIndex } = props;
    if (isBefore) {
      overIndex -= 1;
    }
    const resultingOverIndex = overIndex >= draggedIndex ? overIndex : overIndex + 1;
    if (draggedIndex !== resultingOverIndex) {
      props.move(draggedIndex, resultingOverIndex);
    }
  },
});

const makeSortableItem =
  ({ itemType, wrapperStyle = {}, overlayStyle = {} }) =>
  (Component) => {
    const optionSource = {
      beginDrag(props) {
        return { index: props.index };
      },
    };
    const beforeOptionTarget = makeTarget(true);
    const afterOptionTarget = makeTarget(false);

    const Option = ({
      connectDragSource,
      connectBeforeDropTarget,
      connectAfterDropTarget,
      beforeIsOver,
      beforeCanDrop,
      afterIsOver,
      afterCanDrop,
      ...rest
    }) =>
      connectDragSource(
        <div style={{ position: 'relative', ...wrapperStyle }}>
          {/* This is an overlay with two drop targets: for moving before and after */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: -4,
              ...overlayStyle,
            }}
          >
            {connectBeforeDropTarget(
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  cursor: 'move',
                  borderTop:
                    beforeIsOver && beforeCanDrop ? `4px dotted ${orange}` : '4px transparent',
                }}
              />
            )}
            {connectAfterDropTarget(
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  borderBottom:
                    afterIsOver && afterCanDrop ? `4px dotted ${orange}` : '4px transparent',
                }}
              />
            )}
          </div>
          <Component {...rest} />
        </div>
      );
    Option.propTypes = {
      option: PropTypes.string,
      questionType: PropTypes.string,
      remove: PropTypes.func,
    };

    const WithDragSource = DragSource(itemType, optionSource, (connect, monitor) => ({
      connectDragSource: connect.dragSource(),
      isDragging: monitor.isDragging(),
    }))(Option);

    const WithBeforeDropTarget = DropTarget(itemType, beforeOptionTarget, (connect, monitor) => ({
      connectBeforeDropTarget: connect.dropTarget(),
      beforeIsOver: !!monitor.isOver(),
      beforeCanDrop: !!monitor.canDrop(),
    }))(WithDragSource);

    const WithAfterDropTarget = DropTarget(itemType, afterOptionTarget, (connect, monitor) => ({
      connectAfterDropTarget: connect.dropTarget(),
      afterIsOver: !!monitor.isOver(),
      afterCanDrop: !!monitor.canDrop(),
    }))(WithBeforeDropTarget);

    return WithAfterDropTarget;
  };

export default makeSortableItem;
